import React, { useState, useEffect, useContext } from "react";
import { Typography, Avatar, Button, CardContent, Divider, Box, CircularProgress, IconButton } from "@material-ui/core";
import { WhatsApp, Phone, Email, ArrowBack } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { useParams, withRouter } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";

import MuiStylesAppointmentSummary from "~/components/bookingScreen/MuiStylesAppointmentSummary";
import { returnLanguage } from "~/utils/prepareValues";
import Utils from "~/helpers/Utils";
import { getInitials, paymentTypesValidation, handleAddress } from "~/utils/business";
import { formatDateByBusinessLanguage } from "~/utils/dateUtils";
import { AppointmentCancelDialog, FeedbackModalDetailsSchedules } from "~/components/common/dialog/AppointmentDialogs";
import BackendErrorDialog from "~/components/common/dialog/BackendErrorDialog";
import bookingTransactionService from "~/services/bookingTransactionService";
import checkoutServices from "~/services/checkoutServices";
import SpinnerLoading from "~/components/common/spinnerLoading/SpinnerLoading";
import { redirectToWhatsapp, callBusinessPhone, sendEmailToBusiness } from "~/utils/business";
import SlideUpFadeIn from "~/components/common/animations/SlideUpFadeIn";
import { DialogBuilder } from "~/utils/DialogProvider";
import { useApiErrorModal } from "~/contexts/ApiErrorModalContext";
import ModalVerifyAccess from "~/components/common/ModalVerifyAccess";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import dayjs from "dayjs";
import _ from "lodash";

const AppointmentSummaryScreen = (props) => {
	const { paymentMethod, bookingPayment, isPolicyCancellation } = props.location.state || {};
	const { bookingTransactionId, bookingId } = useParams();
	const { business: businessData } = useSelector((store) => store.business);
	const { t, i18n } = useTranslation();
	const { user } = useSelector((store) => store.auth);
	const meStore = useSelector((store) => store.me);
	const dialog = useContext(DialogBuilder);
	const { showApiError } = useApiErrorModal();

	const [business, setBusiness] = useState(null);
	const [reschedule, setReschedule] = useState(business?.rescheduleId || business?.reschedule);
	const [currency, setCurrency] = useState(business?.currency || "BRL");

	const [loading, setLoading] = useState(false);
	const [isCancelling, setIsCancelling] = useState(false);
	const [isConfirming, setIsConfirming] = useState(false);
	const [appointmentData, setAppointmentData] = useState(null);
	const [isMobile, setIsMobile] = useState(false);
	const [openCancel, setOpenCancel] = useState(false);
	const [openCancelSuccess, setOpenCancelSuccess] = useState(false);
	const [openSuccess, setOpenSuccess] = useState(false);
	const [openRescheduleNotAllowed, setOpenRescheduleNotAllowed] = useState(false);
	const [openErrorCancel, setOpenErrorCancel] = useState(false);
	const [messageError, setMessageError] = useState("");
	const [paymentCancellation, setPaymentCancellation] = useState(false);
	const [showPaymentDetails, setShowPaymentDetails] = useState(false);
	const [animationKey, setAnimationKey] = useState(0);
	const [animationDirection, setAnimationDirection] = useState("up");
	const [showVerifyAccessModal, setShowVerifyAccessModal] = useState(false);

	const currentUser = meStore?.me || user || {};

	const isOwnerUser =
		!_.isEmpty(currentUser?.id) && !_.isEmpty(business?.ownerId) && currentUser.id === business?.ownerId;

	const searchParams = new URLSearchParams(props.location.search);
	const rawSource = searchParams.get("source");
	const origemLink = rawSource === "lembrete" ? "lembrete" : rawSource === "confirmacao" ? "confirmacao" : "outro";
	const temSessaoAtiva = !_.isEmpty(currentUser?.id);

	const trackAcessoNegado = (tela) => {
		mixpanelTrackEvent(
			"Acesso_Negado_Reserva",
			{
				company_id: business?.id,
				origem_link: origemLink,
				sessao_e_do_estabelecimento: isOwnerUser,
				tem_sessao_ativa: temSessaoAtiva,
				tela,
			},
			currentUser,
			business
		);
	};

	const goToPaymentDetails = () => {
		setAnimationDirection("right");
		setAnimationKey((prev) => prev + 1);
		setShowPaymentDetails(true);
	};

	const goBackToSummary = () => {
		setAnimationDirection("left");
		setAnimationKey((prev) => prev + 1);
		setShowPaymentDetails(false);
	};

	const classes = MuiStylesAppointmentSummary();

	const scheduleDay = moment(appointmentData?.startTime);
	const confirmFlag = moment.duration(scheduleDay.diff(moment(new Date()))).asDays();

	const isFutureBooking = confirmFlag > 0;
	const isSameDayBooking = confirmFlag > 0 && confirmFlag < 1;
	const isActive = appointmentData?.status === "active";
	const isWaiting = appointmentData?.status === "waiting";
	const isCancelledOrRefused = ["cancelled", "refuse"].includes(appointmentData?.status);
	const canConfirm = isSameDayBooking && !isCancelledOrRefused && !isWaiting && !appointmentData?.confirmReservation;
	const canReschedule = isFutureBooking && !isCancelledOrRefused && reschedule;
	const canCancel = isFutureBooking && !isCancelledOrRefused;
	const showButtons = canConfirm || canReschedule || canCancel;

	const onConfirmBooking = async () => {
		const dadosInvalidos = !appointmentData?.bookingId || !appointmentData?.status;
		if (dadosInvalidos) {
			throw new Error("Dados de reserva inválidos");
		}
		setIsConfirming(true);
		try {
			const bookingCheck = await bookingTransactionService.putBookingStatus(
				appointmentData?.bookingId,
				"",
				appointmentData?.status,
				null,
				true
			);
			if (bookingCheck) {
				setOpenSuccess(true);
			}
		} catch (data) {
			if (!showApiError(data)) {
				if (data && data.response && data.response.status === 400) {
					dialog.withMessage(t("Invalid booking data. Please check the information provided and try again!")).show();
				} else if (data && data.response && data.response.status === 403) {
					if (data?.response?.data?.code === "booking_owner_mismatch") {
						trackAcessoNegado("Confirmacao_Reserva");
					}
					setShowVerifyAccessModal(true);
				} else {
					dialog.withMessage(`${data?.message || data?.response?.data?.message || ""} ${t("Try again!")}`).show();
				}
			}
		} finally {
			setIsConfirming(false);
		}
	};

	const onBookingReschedule = async () => {
		if (!appointmentData) {
			console.error("appointmentData is not available");
			return;
		}

		setLoading(true);

		if (!reschedule) {
			setLoading(false);
			return;
		}

		try {
			const { data } = await bookingTransactionService.getBookingRescheduleValidByBookingId(
				appointmentData?.bookingId
			);

			if (data === false) {
				setLoading(false);
				setOpenRescheduleNotAllowed(true);
				return;
			}

			const serviceIds = [
				{
					featureItems: appointmentData?.featureItems || [],
					id: appointmentData?.serviceId,
					serviceName: appointmentData?.name,
				},
			];

			const rescheduleData = {
			bookingId: appointmentData?.bookingId,
			businessId: business?.id,
			professionalId: appointmentData?.professionalId,
			serviceId: appointmentData?.serviceId,
			isPackageReschedule: !!appointmentData?.packageId,
			};

			props.history.push(`/selecionar-data/${business?.id}`, {
			services: serviceIds,
			bookingId: appointmentData?.bookingTransactionId,
			reservationUpdate: true,
			reschedule: rescheduleData,
			});

		} catch (error) {
			if (!showApiError(error)) {
				const errorMessage = error?.response?.data?.message || error?.message || "Unknown error";
				dialog.withMessage(`${errorMessage} ${t("Try again!")}`).show();
			}
			setLoading(false);
		}
	};

	const cancel = async () => {
		if (!business || !appointmentData) {
			dialog
				.withTitle(t("Data not loaded"))
				.withMessage(t("Unable to cancel Please refresh the page and try again"))
				.withAction(() => window.location.reload())
				.show();
			return;
		}
		const obj = {
			recipient_id: business?.id,
			booking_transaction_id: appointmentData?.bookingTransactionId,
			date_booking_created: appointmentData?.createdAt,
			service_id: appointmentData?.id,
		};
		try {
			setIsCancelling(true);
			const { data } = await checkoutServices.fetchCancellation(obj);
			setPaymentCancellation(data ?? null);
			setTimeout(() => {
				setOpenCancel(true);
				setIsCancelling(false);
			}, 500);
		} catch (error) {
			setIsCancelling(false);
			if (!showApiError(error)) {
				if (error && error.response && error.response.status === 403) {
					if (error?.response?.data?.code === "booking_owner_mismatch") {
						trackAcessoNegado("Cancelamento_Reserva");
					}
					setShowVerifyAccessModal(true);
				} else {
					dialog.withMessage(`${error?.response?.data?.message || error?.message || ""} ${t("Try again!")}`).show();
				}
			}
		}
	};

	const mockData = {
		business: {
			name: business?.name,
			address: business?.address ? handleAddress(business, 35) : "",
			initials: business ? getInitials(business?.name) : "",
		},
		appointment: {
			date: formatDateByBusinessLanguage(
				appointmentData?.startTime || appointmentData?.startTime,
				i18n?.languages?.[1] || "pt",
				true,
				business?.timeZone
			),
			history: {
				clientCreated: `${t("Made by client on")} ${moment(appointmentData?.createdAt).format("DD/MM/YY")}`,
				professionalModified: `${t("Modified by professional")} ${appointmentData?.professional} ${moment(
					appointmentData?.createdAt
				).format("DD/MM/YY")}`,
			},
		},
		professional: {
			name: appointmentData?.professional,
			avatar: appointmentData?.avatarProfessional,
		},
		service: {
			name: appointmentData?.name,
			priceType: appointmentData?.pricingType?.["name" + returnLanguage(business?.language)],
			paymentInfo: `${moment(appointmentData?.createdAt).format("DD/MM/YY")} - ${paymentTypesValidation(
				paymentMethod,
				t
			)}`,
			balance: `${
				appointmentData?.pricingType && Utils.dontShowPricingTypeNames.includes(appointmentData?.pricingType.name)
					? t("Upon consultation")
					: bookingPayment && bookingPayment > 0
					? `${t("Payment made")} ${
							!Utils.fixedPricingTypeNames.includes(appointmentData?.pricingType.name)
								? appointmentData?.pricingType.name
								: ""
					  } ${
							!Utils.freePricingTypeNames.includes(appointmentData?.pricingType.name)
								? Utils.formatMoney((bookingPayment / 100), currency)
								: ""
					  }`
					: ""
			}`,
		},
	};

	useEffect(() => {
		const checkIsMobile = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		checkIsMobile();
		window.addEventListener("resize", checkIsMobile);

		return () => window.removeEventListener("resize", checkIsMobile);
	}, []);

	useEffect(() => {
		let isMounted = true;

		const loadAppointmentData = async () => {
			if (!bookingTransactionId) return;
			if (isMounted) setLoading(true);
			try {
				const { data: transaction } = await bookingTransactionService.getOneBookingTransaction(
					bookingTransactionId,
					businessData?.language
				);

				if (!isMounted) return;

				if (!transaction || !transaction.bookings || transaction.bookings.length === 0) {
					return;
				}

				const booking = bookingId
					? transaction.bookings.find((b) => String(b.id) === String(bookingId)) || transaction.bookings[0]
					: transaction.bookings[0];

				if (!booking) {
					return;
				}

				const appointment = {
					serviceId: booking?.service?.id,
					bookingId: booking?.id,
					bookingTransactionId: transaction?.id,
					startTime: booking?.startTime,
					endTime: booking?.endTime,
					name: booking?.service?.name,
					price: booking?.price,
					professional: booking?.professional?.name,
					professionalId: booking?.professional?.id,
					id: booking?.service?.id,
					featureItems: booking?.service?.featureItems || booking?.service?.feature_items || [],
					status: booking?.status,
					confirmReservation: booking?.confirmReservation,
					createdAt: booking?.createdAt,
					pricingType: booking?.pricingType,
					avatarProfessional: booking?.professional?.avatarUrl || booking?.professional?.avatar_url || null,
					packageId: transaction?.packageId || null,
				};

				if (isMounted) {
					setBusiness(businessData || booking?.business);
					setCurrency(booking?.business?.currency || "BRL");
					setReschedule(booking?.business?.rescheduleId || false);
					setAppointmentData(appointment);
					setLoading(false);
				}
			} catch (error) {
				if (!isMounted) return;
				setLoading(false);
				if (error?.response?.status === 403 && error?.response?.data?.code === "booking_owner_mismatch") {
					trackAcessoNegado("Resumo_Reserva");
					setShowVerifyAccessModal(true);
				} else if (!showApiError(error)) {
					dialog.withMessage(`${error?.response?.data?.message || error?.message || ""} ${t("Try again!")}`).show();
				}
			} finally {
				if (isMounted) setLoading(false);
			}
		};

		loadAppointmentData();

		return () => { isMounted = false; };
	}, []);

	if (loading) {
		return <SpinnerLoading loading={loading} />;
	}

	const handleGoBack = () => {
		const goBackRoute = business?.id ? `/reservas/${business.id}` : `/reservas`;
		return props.history.push(goBackRoute);
	};

	return (
		<>
			<div
				className={classes.container}
				style={{
					overflow: "hidden",
					position: "relative",
					minHeight: "100vh",
				}}>
				<IconButton
					onClick={handleGoBack}
					style={{
						position: "absolute",
						top: 12,
						left: 16,
						zIndex: 10,
						color: "#333",
						width: "40px",
						height: "40px",
					}}>
					<ArrowBack />
				</IconButton>
				<SpinnerLoading loading={isCancelling} />
				{!loading && appointmentData && (
					<SlideUpFadeIn key={animationKey} delay={0} duration={0.5} distance={50} direction={animationDirection}>
						<div
							style={{
								position: "absolute",
								left: "50%",
								transform: "translateX(-50%)",
								borderRadius: "14px",
								width: isMobile ? "calc(100% - 32px)" : "440px",
								maxWidth: isMobile ? "calc(100% - 32px)" : "440px",
								top: 28,
								backgroundColor: "#ffffff",
							}}>
							<CardContent style={{ border: "none" }}>
								<Box className={classes.headerSection}>
									<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
										<Avatar
											className={classes.businessAvatar}
											src={business?.thumbnailUrl ? business.thumbnailUrl : ""}>
											<Typography style={{ fontSize: "clamp(30px, 2vw, 32px)" }}>
												{getInitials(business?.name)}
											</Typography>
										</Avatar>
										<div>
											<Typography className={classes.businessName}>{mockData.business.name}</Typography>
											<Typography className={classes.businessAddress}>{mockData.business.address}</Typography>
										</div>
									</div>
								</Box>
								{!showPaymentDetails && (
									<Box className={classes.contactSection}>
										<div className={classes.contactItem}>
											<div className={classes.contactIcon}>
												<WhatsApp
													className={classes.iconSize}
													onClick={() => redirectToWhatsapp(business?.phone, "")}
												/>
											</div>
											<Typography className={classes.contactLabel}>WhatsApp</Typography>
										</div>
										<div className={classes.contactItem}>
											<div className={classes.contactIcon}>
												<Phone className={classes.iconSize} onClick={() => callBusinessPhone(business?.phone)} />
											</div>
											<Typography className={classes.contactLabel}>{t("Phone")}</Typography>
										</div>
										{business?.email && (
											<div className={classes.contactItem}>
												<div className={classes.contactIcon}>
													<Email className={classes.iconSize} onClick={() => sendEmailToBusiness(business?.email)} />
												</div>
												<Typography className={classes.contactLabel}>E-mail</Typography>
											</div>
										)}
									</Box>
								)}
								<Typography className={classes.summaryTitle}>
									{showPaymentDetails ? t("Appointment summary") : t("Your reservation summary")}
								</Typography>
								<Divider style={{ margin: "8px 0" }} />
								<Typography className={classes.appointmentDateTime}>{mockData.appointment.date}</Typography>
								<Box className={classes.professionalSection}>
									<Avatar
										className={classes.professionalAvatar}
										src={mockData.professional.avatar}
										alt={mockData.professional.name}
									/>
									<div className={classes.professionalInfo}>
										<Typography className={classes.professionalName}>{mockData.professional.name}</Typography>
										<Typography className={classes.serviceInfo}>{mockData.service.name}</Typography>
									</div>
								</Box>
								{showPaymentDetails && (
									<Typography className={classes.priceTag}>{mockData.service.priceType}</Typography>
								)}
								{!showPaymentDetails && (
									<Box
										onClick={goToPaymentDetails}
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
											cursor: "pointer",
											marginTop: "8px",
										}}>
										<Typography style={{ fontSize: "16px", color: "#1A73E8", fontWeight: "500" }}>
											{t("View payment details")}
										</Typography>
									</Box>
								)}
								<Divider style={{ margin: "8px 0" }} />
								{paymentTypesValidation(paymentMethod, t) && showPaymentDetails && (
									<Typography className={classes.paymentInfo}>{mockData.service.paymentInfo}</Typography>
								)}
								{showPaymentDetails && (
									<Typography className={classes.balanceInfo}>{mockData.service.balance}</Typography>
								)}
							</CardContent>
						</div>
					</SlideUpFadeIn>
				)}

				<Box className={classes.footerBar}>
					<div>
						{showButtons && !showPaymentDetails && (
							<SlideUpFadeIn key={`footer-${animationKey}`} delay={0.1} duration={0.4} distance={30} direction="up">
								<div>
									<div style={{ display: "flex", alignItems: "center" }}>
										{canReschedule && (
											<Button onClick={() => onBookingReschedule()} className={classes.actionButtonFooter}>
												{t("Change")}
											</Button>
										)}
										{canCancel && (
											<Button
												onClick={() => cancel()}
												className={classes.cancelButtonFooter}
												disabled={isCancelling}
												startIcon={isCancelling ? <CircularProgress size={16} color="inherit" /> : null}>
												{isCancelling ? t("Cancelling") : t("btnCancel")}
											</Button>
										)}
									</div>
									<div>
										{canConfirm && (
											<div style={{ flex: 1 }}>
												<Button
													onClick={() => onConfirmBooking()}
													className={classes.confirmButtonFooter}
													disabled={isConfirming}
													startIcon={isConfirming ? <CircularProgress size={16} color="inherit" /> : null}>
													{isConfirming ? t("Confirming") : t("Confirm")}
												</Button>
											</div>
										)}
									</div>
								</div>
							</SlideUpFadeIn>
						)}
						{showPaymentDetails && (
							<SlideUpFadeIn
								key={`footer-back-${animationKey}`}
								delay={0.1}
								duration={0.4}
								distance={30}
								direction="up">
								<Button onClick={goBackToSummary} className={classes.actionButtonFooter}>
									{t("Back")}
								</Button>
							</SlideUpFadeIn>
						)}
					</div>
				</Box>

				<AppointmentCancelDialog
					open={openCancel}
					onClose={() => setOpenCancel(false)}
					business={business}
					professional={appointmentData?.professional}
					avatarProfessional={appointmentData?.avatarProfessional}
					serviceName={appointmentData?.name}
					startTime={appointmentData?.startTime}
					date={appointmentData?.startTime}
					dateEnd={appointmentData?.endTime}
					pricingType={appointmentData?.pricingType}
					currency={currency}
					paymentCancellation={paymentCancellation}
					bookingId={appointmentData?.bookingId}
					loading={loading}
					history={props.history}
					setOpen={setOpenCancel}
					setOpenErrorCancel={setOpenErrorCancel}
					setMessageError={setMessageError}
					onCancelSuccess={() => setOpenCancelSuccess(true)}
					isOwnerUser={isOwnerUser}
					currentUser={currentUser}
					origemLink={origemLink}
				/>
				<FeedbackModalDetailsSchedules
					open={openSuccess || openCancelSuccess}
					onClose={() => { setOpenSuccess(false); setOpenCancelSuccess(false); }}
					business={business}
					history={props.history}
					confirmSchedule={openSuccess}
					cancelSchedule={openCancelSuccess}
					title={ openSuccess ? t("Thanks for confirming") : null}
					message={ openSuccess ? t("message feedback confirm", {
						businessName: business?.name,
						professionalName: mockData?.professional?.name,
						date: i18n.language === "en-US" ? dayjs(appointmentData?.startTime).format("MM/DD/YYYY") : dayjs(appointmentData?.startTime).format("DD/MM/YYYY"),
						time: dayjs(appointmentData?.startTime).utc().tz(business?.timeZone).format("HH:mm")
					}) : null}
				/>
				<FeedbackModalDetailsSchedules
					open={openRescheduleNotAllowed}
					onClose={() => setOpenRescheduleNotAllowed(false)}
					business={business}
					history={props.history}
					alert
					cancelSchedule={false}
					title={t("Reschedule not allowed")}
					message={t("message reschedule not allowed")}
				/>
				<ModalVerifyAccess
					open={showVerifyAccessModal}
					onClose={() => setShowVerifyAccessModal(false)}
				/>
				<BackendErrorDialog
					open={openErrorCancel}
					onClose={() => setOpenErrorCancel(false)}
					message={messageError}
					business={business}
					history={props.history}
				/>
			</div>
		</>
	);
};

export default withRouter(AppointmentSummaryScreen);
